import React from 'react';
import styled from 'styled-components';
import { ArrayPath, Control, FieldArray, FieldValues, useFieldArray } from 'react-hook-form';
import { Button, IconButton, InputLabel } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';

interface RepeatableRowsEditorProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: ArrayPath<TFieldValues>;
  label: string;
  addButtonLabel?: string;
  maxRows?: number;
  disabled?: boolean;
  emptyMessage?: string;
  createEmptyRow: () => FieldArray<TFieldValues, ArrayPath<TFieldValues>>;
  // renderRow receives the row's field-path prefix (`${name}.${index}`) to address its fields.
  renderRow: (rowName: string, index: number) => React.ReactNode;
}

// Generic add / remove / reorder editor over a react-hook-form useFieldArray.
function RepeatableRowsEditor<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  addButtonLabel = 'Add row',
  maxRows,
  disabled,
  emptyMessage = 'No items yet.',
  createEmptyRow,
  renderRow,
}: RepeatableRowsEditorProps<TFieldValues>) {
  const { fields, append, remove, move } = useFieldArray({ control, name });
  const reachedMax = maxRows !== undefined && fields.length >= maxRows;

  return (
    <StyledContainer>
      <InputLabel>{label}</InputLabel>
      <StyledRowsList>
        {fields.length === 0 && <StyledEmptyState>{emptyMessage}</StyledEmptyState>}
        {fields.map((field, index) => (
          <StyledRow key={field.id}>
            <StyledRowFields>{renderRow(`${name}.${index}`, index)}</StyledRowFields>
            <StyledRowControls>
              <IconButton
                type="button"
                aria-label="Move up"
                disabled={disabled || index === 0}
                onClick={() => move(index, index - 1)}
              >
                <KeyboardArrowUp />
              </IconButton>
              <IconButton
                type="button"
                aria-label="Move down"
                disabled={disabled || index === fields.length - 1}
                onClick={() => move(index, index + 1)}
              >
                <KeyboardArrowDown />
              </IconButton>
              <IconButton type="button" aria-label="Remove" disabled={disabled} onClick={() => remove(index)}>
                <Close />
              </IconButton>
            </StyledRowControls>
          </StyledRow>
        ))}
      </StyledRowsList>
      <Button
        type="button"
        variant="outlined"
        disabled={disabled || reachedMax}
        onClick={() => append(createEmptyRow())}
      >
        {addButtonLabel}
      </Button>
    </StyledContainer>
  );
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 30px;
  width: 100%;
`;

const StyledRowsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const StyledRowFields = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledRowControls = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledEmptyState = styled.div`
  color: #757575;
  font-style: italic;
`;

export default RepeatableRowsEditor;
